import {
  type ReactNode,
  Suspense,
  lazy,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Button,
  IconButton,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import { MinusIcon, PlusIcon, SparklesIcon } from 'lucide-react';

import { buildImageTransform } from '@modules/_shared/components/image-upload/buildImageTransform';
import { ImageLoadingOverlay } from '@modules/_shared/components/image-upload/components/ImageLoadingOverlay';
import type {
  CropArea,
  ImageSource,
  ImageTransform,
} from '@modules/_shared/components/image-upload/types';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';

const Cropper = lazy(() => import('react-easy-crop'));

type ImageUploadProps = {
  source: ImageSource;
  currentImageUrl?: string | null;
  open: boolean;
  onClose: () => void;
  onSave: (transform: ImageTransform) => void;
  onChangePhoto?: () => void;
  isLoading?: boolean;
  aspectRatio?: number;
  cropShape?: 'rect' | 'round';
  minZoom?: number;
  maxZoom?: number;
  title: string;
  enableCropping?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  infoText?: ReactNode;
  onEnhanceClick?: () => void;
};

export const ImageUpload = memo(
  ({
    source,
    currentImageUrl,
    open,
    onClose,
    onSave,
    onChangePhoto,
    isLoading = false,
    aspectRatio = 1,
    cropShape = 'rect',
    minZoom = 1,
    maxZoom = 3,
    title,
    enableCropping = true,
    maxWidth = 'sm',
    infoText,
    onEnhanceClick,
  }: ImageUploadProps) => {
    const { t } = useLocale();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(minZoom);
    const [rotation, setRotation] = useState(0);
    const [croppedArea, setCroppedArea] = useState<CropArea | null>(null);
    const [isImageLoading, setIsImageLoading] = useState(true);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const isInitialMount = useRef(true);
    const imageUrlRef = useRef<string | null>(null);

    const imageUrl = useMemo(() => {
      // Clean up previous object URL
      if (imageUrlRef.current && imageUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrlRef.current);
      }

      let url = '';
      if (source.type === 'file') {
        url = URL.createObjectURL(source.data as File);
        imageUrlRef.current = url;
      } else if (source.type === 'url') {
        url = source.data as string;
      } else {
        url = currentImageUrl || '';
      }

      return url;
    }, [source, currentImageUrl]);

    useEffect(() => {
      setCrop({ x: 0, y: 0 });
      setZoom(minZoom);
      setRotation(0);
      setCroppedArea(null);
      setIsImageLoading(true);
      // Reset interaction state when source changes
      setHasUserInteracted(false);
      isInitialMount.current = true;
    }, [source, minZoom]);

    // Cleanup object URLs on unmount
    useEffect(() => {
      return () => {
        if (imageUrlRef.current && imageUrlRef.current.startsWith('blob:')) {
          URL.revokeObjectURL(imageUrlRef.current);
        }
      };
    }, []);

    const handleImageLoad = useCallback(() => {
      setIsImageLoading(false);
      // Set initial mount to false after image loads
      setTimeout(() => {
        isInitialMount.current = false;
      }, 100);
    }, []);

    const onCropComplete = useCallback(
      (_: CropArea, croppedAreaPixels: CropArea) => {
        setCroppedArea(croppedAreaPixels);
      },
      [],
    );

    const handleSetRotation = useCallback((value: number | number[]) => {
      setRotation(value as number);
    }, []);

    const handleSave = useCallback(() => {
      onSave(
        buildImageTransform({
          hasUserInteracted,
          cropArea: croppedArea,
          scale: zoom,
          initialScale: 1,
          rotation,
        }),
      );
    }, [croppedArea, rotation, zoom, hasUserInteracted, onSave]);

    const footerContent = useMemo(
      () => (
        <Stack spacing={2} sx={{ width: '100%' }}>
          {onChangePhoto && (
            <Button
              variant="text"
              onClick={onChangePhoto}
              sx={{ color: 'primary.main' }}
            >
              {t('Change photo')}
            </Button>
          )}
        </Stack>
      ),
      [onChangePhoto, t],
    );

    return (
      <SlideDialog
        open={open}
        onClose={onClose}
        title={title}
        onSave={handleSave}
        loading={isLoading}
        maxWidth={maxWidth}
        fullWidth
        secondaryActions={footerContent}
        cancelText={t('Cancel')}
        saveText={t('Save')}
      >
        <Box
          sx={{
            position: 'relative',
            height: '50vh',
            maxHeight: '600px',
          }}
          role="img"
        >
          <Suspense fallback={<LoadingSpinner />}>
            {enableCropping ? (
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspectRatio}
                cropShape={cropShape}
                onCropChange={newCrop => {
                  setCrop(newCrop);
                  // Only set interaction if not during initial mount
                  if (!isInitialMount.current) {
                    setHasUserInteracted(true);
                  }
                }}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onMediaLoaded={handleImageLoad}
                minZoom={minZoom}
                maxZoom={maxZoom}
                showGrid={false}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={imageUrl}
                  alt={title}
                  onLoad={handleImageLoad}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
            <ImageLoadingOverlay visible={isImageLoading} />
          </Suspense>
          {onEnhanceClick && (
            <Button
              onClick={onEnhanceClick}
              variant="contained"
              size="small"
              startIcon={<SparklesIcon />}
              sx={theme => ({
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 10,
                backgroundColor: 'rgba(255,255,255,0.95)',
                color: 'primary.main',
                boxShadow: theme.shadows[6],
                backdropFilter: 'blur(8px)',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                ...theme.applyStyles('dark', {
                  backgroundColor: 'rgba(30,34,42,0.95)',
                }),
                '&:hover': {
                  backgroundColor: '#fff',
                  ...theme.applyStyles('dark', {
                    backgroundColor: 'rgba(30,34,42,1)',
                  }),
                },
                '&:active': {
                  boxShadow: theme.shadows[1],
                  transform: 'translateY(1px)',
                },
              })}
            >
              {t('Enhance with AI')}
            </Button>
          )}
        </Box>
        {enableCropping && (
          <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1">
                  {t('Zoom')} {zoom.toFixed(1)}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newZoom = Math.max(minZoom, zoom - 0.1);
                      setZoom(newZoom);
                      if (newZoom !== minZoom) {
                        setHasUserInteracted(true);
                      }
                    }}
                    disabled={zoom <= minZoom}
                  >
                    <MinusIcon size={20} />
                  </IconButton>
                  <Slider
                    value={zoom}
                    onChange={(_, value) => {
                      setZoom(value as number);
                      setHasUserInteracted(true);
                    }}
                    min={minZoom}
                    max={maxZoom}
                    step={0.1}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newZoom = Math.min(maxZoom, zoom + 0.1);
                      setZoom(newZoom);
                      setHasUserInteracted(true);
                    }}
                    disabled={zoom >= maxZoom}
                  >
                    <PlusIcon size={20} />
                  </IconButton>
                </Stack>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="body1">
                  {t('Rectifier')} {rotation}°
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newRotation = Math.max(-180, rotation - 10);
                      setRotation(newRotation);
                      if (newRotation !== 0) {
                        setHasUserInteracted(true);
                      }
                    }}
                    disabled={rotation <= -180}
                  >
                    <MinusIcon size={20} />
                  </IconButton>
                  <Slider
                    value={rotation}
                    onChange={(_, value) => {
                      handleSetRotation(value);
                      setHasUserInteracted(true);
                    }}
                    min={-180}
                    max={180}
                    step={1}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newRotation = Math.min(180, rotation + 10);
                      setRotation(newRotation);
                      if (newRotation !== 0) {
                        setHasUserInteracted(true);
                      }
                    }}
                    disabled={rotation >= 180}
                  >
                    <PlusIcon size={20} />
                  </IconButton>
                </Stack>
              </Box>
            </Box>
            {infoText && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  textAlign: 'left',
                }}
              >
                {infoText}
              </Typography>
            )}
          </Box>
        )}
      </SlideDialog>
    );
  },
);
